import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Typography,
} from "@material-ui/core";
import {
  Description,
  ExpandLess,
  ExpandMore,
  Gavel,
  Dashboard as DashboardIcon,
  People,
  Assessment,
  BarChart,
  Storage,
  VideoLibrary,
} from "@material-ui/icons";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/ApiKeyContext";
import { useUserHasProduct } from "../hooks/useUserHasProduct";
import { useTranslation } from "../hooks/useTranslation";

const drawerWidth = 260;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "normal",
    position: "fixed",
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: "var(--ikaro-bg-surface)",
    borderRight: "1px solid var(--ikaro-gray-medium)",
    overflowX: "hidden",
    position: "fixed",
    boxShadow: "var(--ikaro-shadow-light)", // Sombra sutil
    transition: theme.transitions.create("transform", {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
  drawerOpen: {
    transform: "translateX(0)",
  },
  drawerClose: {
    transform: "translateX(-100%)",
    borderRight: "none",
  },
  toolbar: theme.mixins.toolbar,
  nested: {
    paddingLeft: theme.spacing(4),
  },
  listItemIcon: {
    minWidth: 40,
    color: "var(--ikaro-gray-dark)",
  },
  menuText: {
    whiteSpace: "normal",
    wordWrap: "break-word",
  },
  // Novo estilo para itens pais - limpo e moderno
  parentMenuItem: {
    margin: "2px 8px",
    borderRadius: "8px",
    transition: "all 0.2s ease-in-out",
    "& .MuiListItemText-primary": {
      fontWeight: "600",
      fontSize: "0.9rem",
      color: "var(--ikaro-text-dark)",
      whiteSpace: "normal",
    },
    "& .MuiListItemIcon-root": {
      color: "var(--ikaro-gray-dark)",
    },
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
      transform: "translateX(2px)",
      "& .MuiListItemIcon-root": {
        color: "var(--ikaro-orange-primary)",
      },
    },
  },
  // Submenu items com estilo moderno
  submenuItem: {
    margin: "2px 16px 2px 24px",
    borderRadius: "6px",
    paddingLeft: theme.spacing(3),
    transition: "all 0.2s ease-in-out",
    "& .MuiListItemText-primary": {
      fontSize: "0.85rem",
      color: "var(--ikaro-gray-dark)",
      whiteSpace: "normal",
    },
    "& .MuiListItemIcon-root": {
      color: "var(--ikaro-gray-medium)",
      minWidth: 35,
    },
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
      transform: "translateX(2px)",
      "& .MuiListItemIcon-root": {
        color: "var(--ikaro-orange-primary)",
      },
      "& .MuiListItemText-primary": {
        color: "var(--ikaro-text-dark)",
      },
    },
  },
  // Submenu item ativo - estilo elegante
  submenuItemActive: {
    margin: "2px 16px 2px 24px",
    borderRadius: "6px",
    paddingLeft: theme.spacing(3),
    backgroundColor: "var(--ikaro-orange-light)",
    boxShadow: "var(--ikaro-shadow-light)",
    transform: "translateX(2px)",
    "& .MuiListItemIcon-root": {
      color: "var(--ikaro-orange-primary)",
      minWidth: 35,
    },
    "& .MuiListItemText-primary": {
      fontWeight: "600",
      fontSize: "0.85rem",
      color: "var(--ikaro-orange-primary)",
      whiteSpace: "normal",
    },
  },
  // Dashboard item normal
  dashboardItem: {
    margin: "4px 8px",
    borderRadius: "8px",
    transition: "all 0.2s ease-in-out",
    "& .MuiListItemText-primary": {
      fontWeight: "600",
      fontSize: "0.9rem",
      color: "var(--ikaro-text-dark)",
      whiteSpace: "normal",
    },
    "& .MuiListItemIcon-root": {
      color: "var(--ikaro-gray-dark)",
    },
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
      transform: "translateX(2px)",
      "& .MuiListItemIcon-root": {
        color: "var(--ikaro-orange-primary)",
      },
    },
  },
  // Dashboard item ativo
  dashboardItemActive: {
    margin: "4px 8px",
    borderRadius: "8px",
    backgroundColor: "var(--ikaro-orange-light)",
    boxShadow: "var(--ikaro-shadow-light)",
    transform: "translateX(2px)",
    "& .MuiListItemIcon-root": {
      color: "var(--ikaro-orange-primary)",
    },
    "& .MuiListItemText-primary": {
      fontWeight: "700",
      fontSize: "0.9rem",
      color: "var(--ikaro-orange-primary)",
      whiteSpace: "normal",
    },
  },
  expandIcon: {
    color: "var(--ikaro-gray-dark)",
    transition: "all 0.2s ease-in-out",
  },
  collapsibleSection: {
    marginBottom: theme.spacing(1),
  },
  // Divider customizado
  customDivider: {
    margin: "12px 16px",
    backgroundColor: "var(--ikaro-gray-medium)",
    height: "1px",
  },
}));

const SideMenu = ({ open, setOpen }) => {
  const classes = useStyles();
  const location = useLocation();
  const { hasRole } = useAuth();
  const { t } = useTranslation("sidemenu");
  const { loading: loadingDossieProduct, hasProduct: hasDossieProduct } =
    useUserHasProduct("DC_CONTESTACAO_DOSSIE");

  // Estado para controlar a abertura de submenus
  const [openMenus, setOpenMenus] = useState({
    processamento: true, // Inicialmente aberto
    criadorPecas: true, // Mudança: agora também inicialmente aberto
    legalAssistant: false, // Inicialmente fechado
  });

  // Função para alternar a abertura/fechamento de um submenu
  const handleToggleSubmenu = (menu) => {
    setOpenMenus({
      ...openMenus,
      [menu]: !openMenus[menu],
    });
  };

  // Função para fechar o drawer após clicar em um item
  const handleMenuItemClick = () => {
    setOpen(false);
  };

  // Verificar se um link está ativo
  const isActive = (path) => {
    return location.pathname === path;
  };

  const isContestationDossieActive = () =>
    location.pathname.startsWith("/contestacao-dossie");

  const drawer = (
    <div>
      <div className={classes.toolbar} />
      <Divider className={classes.customDivider} />

      <List>
        {/* Dashboard Item */}
        <ListItem
          button
          component={NavLink}
          to="/dashboard"
          className={
            isActive("/dashboard")
              ? classes.dashboardItemActive
              : classes.dashboardItem
          }
          onClick={handleMenuItemClick}
        >
          <ListItemIcon className={classes.listItemIcon}>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText primary={t("dashboard")} className={classes.menuText} />
        </ListItem>
        <ListItem
          button
          component={NavLink}
          to="/base-conhecimento"
          className={
            isActive("/base-conhecimento")
              ? classes.dashboardItemActive
              : classes.dashboardItem
          }
          onClick={handleMenuItemClick}
        >
          <ListItemIcon className={classes.listItemIcon}>
            <VideoLibrary />
          </ListItemIcon>
          <ListItemText
            primary={t("knowledgeBase")}
            className={classes.menuText}
          />
        </ListItem>
        {/* Menu de Processamento de Documentos */}
        {hasRole("PROCESS_DIGITAL_PDF", "view_jobs") && (
          <>
            <Divider className={classes.customDivider} />
            <div className={classes.collapsibleSection}>
              <ListItem
                button
                onClick={() => handleToggleSubmenu("processamento")}
                className={classes.parentMenuItem}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Description />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      className={classes.menuText}
                    >
                      {t("documentProcessing")}
                    </Typography>
                  }
                />
                {openMenus.processamento ? (
                  <ExpandLess className={classes.expandIcon} />
                ) : (
                  <ExpandMore className={classes.expandIcon} />
                )}
              </ListItem>
              <Collapse
                in={openMenus.processamento}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  <ListItem
                    button
                    component={NavLink}
                    to="/processamentos"
                    className={
                      isActive("/processamentos")
                        ? classes.submenuItemActive
                        : classes.submenuItem
                    }
                    onClick={handleMenuItemClick}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <Description />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("processings")}
                      className={classes.menuText}
                    />
                  </ListItem>
                </List>
              </Collapse>
            </div>
          </>
        )}
        {/* Menu de Criador Inteligente de Peças */}
        {hasRole("DOCUMENT_CREATION", "view_jobs") && (
          <>
            <Divider className={classes.customDivider} />
            <div className={classes.collapsibleSection}>
              <ListItem
                button
                onClick={() => handleToggleSubmenu("criadorPecas")}
                className={classes.parentMenuItem}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Gavel />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      className={classes.menuText}
                    >
                      {t("intelligentPieceCreator")}
                    </Typography>
                  }
                />
                {openMenus.criadorPecas ? (
                  <ExpandLess className={classes.expandIcon} />
                ) : (
                  <ExpandMore className={classes.expandIcon} />
                )}
              </ListItem>
              <Collapse
                in={openMenus.criadorPecas}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {!loadingDossieProduct && hasDossieProduct && (
                    <ListItem
                      button
                      component={NavLink}
                      to="/contestacao-dossie"
                      className={
                        isContestationDossieActive()
                          ? classes.submenuItemActive
                          : classes.submenuItem
                      }
                      onClick={handleMenuItemClick}
                    >
                      <ListItemIcon className={classes.listItemIcon}>
                        <Gavel />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("contestationDossier")}
                        className={classes.menuText}
                      />
                    </ListItem>
                  )}
                  <ListItem
                    button
                    component={NavLink}
                    to="/contestacoes"
                    className={
                      isActive("/contestacoes")
                        ? classes.submenuItemActive
                        : classes.submenuItem
                    }
                    onClick={handleMenuItemClick}
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <Gavel />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("contestations")}
                      className={classes.menuText}
                    />
                  </ListItem>
                </List>
              </Collapse>
            </div>
          </>
        )}
        {/* Relatórios de Consumo (billing): super admin OU admin de empresa
            com a regra dedicada view_consumption */}
        {(hasRole("ADMIN", "admin") ||
          hasRole("CONSUMPTION_REPORT", "view_consumption")) && (
          <>
            <Divider className={classes.customDivider} />
            <ListItem
              button
              component={NavLink}
              to="/relatorios-consumo"
              className={
                isActive("/relatorios-consumo")
                  ? classes.dashboardItemActive
                  : classes.dashboardItem
              }
              onClick={handleMenuItemClick}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <BarChart />
              </ListItemIcon>
              <ListItemText
                primary={t("consumptionReports")}
                className={classes.menuText}
              />
            </ListItem>
          </>
        )}
        {/* Menu de Visualização de Status de Tarefas */}
        {(hasRole("ADMIN", "admin") ||
          hasRole("DOCUMENT_CREATION", "global_view_jobs_status") ||
          hasRole("PROCESS_DIGITAL_PDF", "global_view_jobs_status")) && (
          <>
            <Divider className={classes.customDivider} />
            <ListItem
              button
              component={NavLink}
              to="/status-tarefas"
              className={
                isActive("/status-tarefas")
                  ? classes.dashboardItemActive
                  : classes.dashboardItem
              }
              onClick={handleMenuItemClick}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <Assessment />
              </ListItemIcon>
              <ListItemText
                primary={t("jobStatusViewer")}
                className={classes.menuText}
              />
            </ListItem>
          </>
        )}
        {(hasRole("ADMIN", "admin") || hasRole("ADMIN", "ambients")) && (
          <>
            <Divider className={classes.customDivider} />
            <ListItem
              button
              component={NavLink}
              to="/gestao-ambientes"
              className={
                isActive("/gestao-ambientes")
                  ? classes.dashboardItemActive
                  : classes.dashboardItem
              }
              onClick={handleMenuItemClick}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <Storage />
              </ListItemIcon>
              <ListItemText
                primary={t("environmentManagement")}
                className={classes.menuText}
              />
            </ListItem>
          </>
        )}
        <Divider className={classes.customDivider} />
        {/* Menu de Gerenciamento de Usuários */}
        {hasRole("USER_MANAGER", "user_manager") && (
          <ListItem
            button
            component={NavLink}
            to="/usuarios"
            className={
              isActive("/usuarios")
                ? classes.dashboardItemActive
                : classes.dashboardItem
            }
            onClick={handleMenuItemClick}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <People />
            </ListItemIcon>
            <ListItemText
              primary={t("userManagement")}
              className={classes.menuText}
            />
          </ListItem>
        )}
      </List>
    </div>
  );

  return (
    <Drawer
      variant="permanent"
      className={`${classes.drawer} ikaro-app-sidebar-drawer`}
      classes={{
        paper: `${classes.drawerPaper} ${
          open ? classes.drawerOpen : classes.drawerClose
        }`,
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default SideMenu;
